import React from 'react';
import { Mail } from 'lucide-react';

interface FollowupEmailOptInProps {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  /** Address on file for the PoS, or null when none. */
  posEmail: string | null;
  /** Manual recipient when the PoS has no email, or an override. */
  recipientOverride: string;
  onRecipientOverrideChange: (email: string) => void;
}

/**
 * Opt-in for the automated post-visit mail. Agents can type a recipient when the PoS
 * has no contact email — otherwise the checkbox would stay permanently disabled.
 */
export const FollowupEmailOptIn: React.FC<FollowupEmailOptInProps> = ({
  enabled,
  onChange,
  posEmail,
  recipientOverride,
  onRecipientOverrideChange,
}) => {
  const typed = recipientOverride.trim();
  const effectiveRecipient = typed || posEmail;
  const canSend = Boolean(effectiveRecipient);

  return (
    <div className="border-2 border-teal-200 bg-teal-50/40 rounded-xl p-4 space-y-3">
      <label
        className={`flex items-start gap-3 select-none ${
          canSend ? 'cursor-pointer' : 'cursor-default'
        }`}
      >
        <input
          type="checkbox"
          checked={enabled && canSend}
          disabled={!canSend}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-1 h-4 w-4 rounded border-gray-300 text-brand-600
            focus:ring-brand-500 disabled:opacity-50"
        />
        <span>
          <span className="flex items-center gap-2 text-sm font-semibold text-gray-900">
            <Mail size={16} className="text-teal-700" />
            Nachfass-E-Mail automatisch senden
          </span>
          {canSend ? (
            <span className="block text-xs text-gray-600 mt-0.5">
              Geht nach dem Speichern an {effectiveRecipient} — mit den Produkten aus
              diesem Besuch, Preisen und Neukundenaktion. Eine Kopie geht an
              mail@brandcircuit.de.
            </span>
          ) : (
            <span className="block text-xs text-amber-800 mt-0.5">
              Für diesen PoS ist keine Kontakt-E-Mail hinterlegt. Adresse unten
              eintragen, dann kannst du die Mail mitsenden.
            </span>
          )}
        </span>
      </label>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          {posEmail ? 'Andere Empfänger-Adresse (optional)' : 'Empfänger-E-Mail'}
        </label>
        <input
          type="email"
          value={recipientOverride}
          onChange={(e) => {
            onRecipientOverrideChange(e.target.value);
            if (e.target.value.trim() && !enabled) onChange(true);
          }}
          placeholder={posEmail || 'name@firma.de'}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white
            focus:ring-2 focus:ring-brand-500 outline-none"
        />
        {posEmail && !typed && (
          <p className="text-xs text-gray-400 mt-1">
            Standard: {posEmail}
          </p>
        )}
      </div>
    </div>
  );
};
